import type { ReactNode } from 'react';

/**
 * Marks the part of a string that matched the search.
 *
 * Substring only, on purpose. Search also matches fuzzily — "enqiry" finds "enquiry" —
 * and there is no honest way to point at which letters did that: the match is a
 * similarity score over the whole run of words, not a span. Highlighting the nearest
 * looking thing would be a guess presented as a fact, so a fuzzy hit is simply left
 * unmarked and the card still shows up.
 */
export function Highlight({ text, term }: { text: string; term: string }): ReactNode {
  const needle = term.trim();
  if (needle.length < 2) return text;

  const parts = splitOn(text, needle);
  if (parts.length === 1) return text;

  return parts.map((part, index) =>
    part.match ? (
      <mark key={index} className="search-hit">
        {part.text}
      </mark>
    ) : (
      part.text
    ),
  );
}

/**
 * Case-insensitive split that keeps the ORIGINAL casing of every piece.
 *
 * Written by index rather than with a RegExp built from the term: the term is whatever
 * someone typed, and `(`, `*` or `[` would either throw or quietly match something
 * else entirely. Escaping it is possible; not building one is simpler.
 */
function splitOn(text: string, needle: string): Array<{ text: string; match: boolean }> {
  const haystack = text.toLowerCase();
  const lower = needle.toLowerCase();
  const parts: Array<{ text: string; match: boolean }> = [];

  let cursor = 0;
  for (;;) {
    const at = haystack.indexOf(lower, cursor);
    if (at === -1) break;

    if (at > cursor) parts.push({ text: text.slice(cursor, at), match: false });
    parts.push({ text: text.slice(at, at + needle.length), match: true });
    cursor = at + needle.length;
  }

  if (cursor < text.length) parts.push({ text: text.slice(cursor), match: false });
  return parts.length > 0 ? parts : [{ text, match: false }];
}

/**
 * Whether the term IS this card's reference, however it was typed.
 *
 * "s444", "S 444" and a bare "444" all mean S-444, so none of them appear inside the
 * reference as a substring. Comparing the normalised forms is what lets the chip be
 * marked at all.
 */
export function matchesReference(reference: string, term: string): boolean {
  const strip = (value: string) => value.replace(/[^a-z0-9]/gi, '').toLowerCase();
  const cleaned = strip(term);
  if (cleaned.length === 0) return false;

  const full = strip(reference);
  // A bare number matches the numeric half — the desk prefix was never typed.
  return cleaned === full || cleaned === full.replace(/^[a-z]+/, '');
}
